<template>
  <div v-show="isYandexAuthEnabled" class="yandex-auth-container">
    <UButton
      :label="buttonLabel"
      color="black"
      block
      :size="size"
      class="h-12"
      :ui="{ rounded: 'rounded-xl' }"
      :loading="isLoading"
      :disabled="disabled"
      @click="handleClick"
    >
      <template #leading>
        <IconTheYandex :class="iconClass" />
      </template>
    </UButton>

    <!-- Модальное окно для Safari/iOS когда popup блокируется -->
    <UModal v-model="showLinkModal" :ui="{ width: 'sm:max-w-md' }">
      <UCard>
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">
              {{ $t('modal.auth_modal_state.yandex.open_title', 'Открыть Яндекс ID') }}
            </h3>
            <UButton color="gray" variant="ghost" icon="i-heroicons-x-mark-20-solid" @click="closeModal" />
          </div>
        </template>

        <div class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{
              $t(
                'modal.auth_modal_state.yandex.instruction',
                'Нажмите на кнопку ниже, чтобы продолжить авторизацию через Яндекс ID. После входа окно закроется автоматически.'
              )
            }}
          </p>

          <UButton color="black" size="lg" block @click="openYandexLink">
            <template #leading>
              <IconTheYandex class="w-6 h-6" />
            </template>
            {{ $t('modal.auth_modal_state.yandex.open_button', 'Открыть Яндекс ID') }}
          </UButton>

          <p class="text-xs text-gray-500 dark:text-gray-500 text-center">
            {{ $t('modal.auth_modal_state.yandex.polling_info', 'Ожидание авторизации продолжается в фоне...') }}
          </p>

          <UButton color="gray" variant="ghost" size="sm" block @click="closeModal">
            {{ $t('common.cancel', 'Отмена') }}
          </UButton>
        </div>
      </UCard>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import { consola } from 'consola';
import { computed, onMounted, watchEffect } from 'vue';
import { useYandexAuthV2 } from '@features/auth';

const props = withDefaults(defineProps<Props>(), {
  size: 'xl',
  label: undefined,
  loadingText: undefined,
  disabled: false,
  // 24px — как у Telegram, MAX и VK: разные иконки распирали кнопки на разную высоту
  iconSize: 'md',
});

const logger = consola.withTag('YandexAuthButton');

/**
 * Переиспользуемый компонент кнопки авторизации через Яндекс ID
 *
 * @example
 * <YandexAuthButton
 *   :size="'xl'"
 *   :loading-text="'Ожидание...'"
 * />
 */

interface Props {
  /** Размер кнопки */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Текст кнопки (по умолчанию из i18n) */
  label?: string;
  /** Текст кнопки во время загрузки */
  loadingText?: string;
  /** Дополнительное условие для disabled */
  disabled?: boolean;
  /** Размер иконки */
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const { t } = useI18n();

logger.log('[YandexAuthButton] 🔵 Component script executing...');

const {
  initYandexAuth,
  openYandexLink: openYandexLinkComposable,
  closeYandexLinkModal,
  isLoading,
  isYandexAuthEnabled,
  showYandexLinkModal,
} = useYandexAuthV2();

logger.log('[YandexAuthButton] 🟢 useYandexAuthV2 called, isYandexAuthEnabled:', isYandexAuthEnabled);

onMounted(() => {
  logger.log('[YandexAuthButton] 🟢 Component MOUNTED! isYandexAuthEnabled.value:', isYandexAuthEnabled.value);
});

// Отслеживаем изменения
watchEffect(() => {
  logger.log('[YandexAuthButton] 🔄 watchEffect: isYandexAuthEnabled.value changed to:', isYandexAuthEnabled.value);
});

/** Размер логотипа: те же ступени, что у UAvatar раньше, но своими классами. */
const ICON_SIZE_CLASS = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
  xl: 'w-8 h-8',
} as const;

const iconClass = computed(() => ICON_SIZE_CLASS[props.iconSize]);

// Вычисляемые свойства
const buttonLabel = computed(() => {
  if (isLoading.value) {
    return props.loadingText || t('modal.auth_modal_state.yandex.loading', 'Ожидание авторизации...');
  }
  return props.label || t('modal.auth_modal_state.yandex.button', 'Войти через Яндекс ID');
});

const showLinkModal = computed({
  get: () => showYandexLinkModal.value,
  set: (value: boolean) => {
    if (!value) {
      closeYandexLinkModal();
    }
  },
});

// Методы
const handleClick = async () => {
  await initYandexAuth();
};

const openYandexLink = () => {
  openYandexLinkComposable();
  // Модальное окно закроется автоматически после успешного открытия
};

const closeModal = () => {
  closeYandexLinkModal();
};

defineExpose({
  handleClick,
  openYandexLink,
  closeModal,
});
</script>

<style scoped lang="scss">
.yandex-auth-container {
  @apply w-full;
}
</style>
